import React, { useEffect } from "react";
import Header from "../../components/ui/Header";
import HeroSection from "./components/HeroSection";
import ProblemSection from "./components/ProblemSection";
import BenefitsSection from "./components/BenefitsSection";
import SolutionSection from "./components/SolutionSection";
import BusinessInActionSection from "./components/BusinessInActionSection";
import TestimonialsSection from "./components/TestimonialsSection";
import AppDownloadSection from "./components/AppDownloadSection";
import FAQSection from "./components/FAQSection";
import Footer from "./components/Footer";
import {
  updatePageSEO,
  injectStructuredData,
  seoConfig,
} from "../../utils/seoConfig";
import useAnalytics from "../../utils/analytics";

const LandingPage = () => {
  // Initialize analytics and tracking
  useAnalytics();

  useEffect(() => {
    try {
      // Use centralized SEO configuration
      updatePageSEO(seoConfig.pages.home);

      // Inject structured data for better SEO
      injectStructuredData(seoConfig.structuredData.organization);
      injectStructuredData(seoConfig.structuredData.mobileApp);
      injectStructuredData(seoConfig.structuredData.website);
    } catch (error) {
      console.warn("Error setting up SEO:", error);
    }
  }, []);

  // Handle scroll-based animations and interactions
  useEffect(() => {
    const handleScroll = () => {
      // Update scroll progress
      const scrolled = window.pageYOffset;
      const maxHeight =
        document.documentElement?.scrollHeight - window.innerHeight;
      const scrollProgress = (scrolled / maxHeight) * 100;

      // Update any scroll-dependent UI elements
      const progressBar = document.querySelector(".scroll-progress");
      if (progressBar) {
        progressBar.style.width = `${scrollProgress}%`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle exit intent for conversion optimization
  useEffect(() => {
    let exitIntentShown = false;

    const handleMouseLeave = (e) => {
      if (e?.clientY <= 0 && !exitIntentShown) {
        exitIntentShown = true;
        // Could trigger exit intent modal or offer
        console.log("Exit intent detected - could show special offer");
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Navigation */}
      <Header />

      {/* Main Content Sections */}
      <main>
        {/* Hero Section - Mobile app introduction and download */}
        <HeroSection />

        {/* Problem Section - Highlight business pain points */}
        <ProblemSection />

        {/* App Features - Core mobile app features */}
        <BenefitsSection />

        {/* Solution in Action - App interface demonstration */}
        <SolutionSection />

        {/* App Screenshots - Visual app demonstration */}
        <BusinessInActionSection />

        {/* Social Proof - User testimonials and reviews */}
        {/* <TestimonialsSecntion /> */}

        {/* App Download - Primary conversion point */}
        <AppDownloadSection />

        {/* FAQ - Common questions about the app */}
        <FAQSection />
      </main>

      {/* Footer with additional links and trust signals */}
      <Footer />

      {/* Performance and Analytics Scripts */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            // Track page load performance
            window.addEventListener('load', function() {
              if (window.performance) {
                const loadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart;
                console.log('Page load time:', loadTime + 'ms');
                
                // Could send to analytics
                if (window.gtag) {
                  window.gtag('event', 'page_load_time', {
                    event_category: 'Performance',
                    value: Math.round(loadTime)
                  });
                }
              }
            });

            // Track scroll depth for engagement
            let maxScroll = 0;
            window.addEventListener('scroll', function() {
              const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
              if (scrollPercent > maxScroll) {
                maxScroll = scrollPercent;
                
                // Track milestone scroll depths
                if ([25, 50, 75, 90].includes(scrollPercent)) {
                  if (window.gtag) {
                    window.gtag('event', 'scroll_depth', {
                      event_category: 'Engagement',
                      event_label: scrollPercent + '%'
                    });
                  }
                }
              }
            });
          `,
        }}
      />
    </div>
  );
};

export default LandingPage;
